import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DayView from './component/DayView';
import WeekView from './component/WeekView';
import MonthView from './component/MonthView';
import YearView from './component/YearView';
import EventModal from './component/EventModal';
import { CategoryColor, IEvent, ViewType } from './core/type';
import { CalendarDays, CalendarIcon, Check, ChevronDown, ChevronLeft, ChevronRight, Filter, LayoutGrid, Plus, Sun, X } from 'lucide-react';
import { useSnackbar } from '../../../../_theme/context/SnackbarContext';
import { Button } from 'react-bootstrap';
import AddorUpdateModal from './component/AddorUpdateModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../redux/store';
import { useAuth } from '../../auth';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { getApiErrorMessage } from "../../../../utils/utils";
import { isAbortedError } from "../../../../utils/global";

import { getEventDates, normalizeDate, parseCalendarQueryDate, parseCalendarQueryEventId } from './core/_utility';
import { CustomDateFormate, getSaTodayDate, SA_TIMEZONE_LABEL } from '../../../../utils/date';
import { getCalenderEvents, getEventCategoryAndColors, getPublicCalendarJsonData } from './core/request';
import { fetchEventAction, fetchEventCategoryAndColorsAction } from '../../../../redux/actions/events.actions';
import SimpleLoader from '../../apps/components/SimpleLoader';

const Calendar: React.FC = () => {
    const { auth } = useAuth();
    const navigate = useNavigate()
    const location = useLocation();
    const { queryDate, queryEventId } = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        return {
            queryDate: parseCalendarQueryDate(searchParams.get('date')),
            queryEventId: parseCalendarQueryEventId(searchParams.get('event_id')),
        };
    }, [location.search]);
    const openedFromQueryRef = useRef<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);
    const dispatch = useDispatch<AppDispatch>();

    const [loading, setLoading] = useState<boolean>(false);

    const { showSnackbar } = useSnackbar();
    const [currentDate, setCurrentDate] = useState<Date>(queryDate ?? getSaTodayDate());
    const [view, setView] = useState<ViewType>('month');
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [eventsForModal, setEventsForModal] = useState<IEvent[]>([]);
    const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
    const [selectedYear, setSelectedYear] = useState<number>(currentDate.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState<number>(currentDate.getMonth());
    const [selectedDay, setSelectedDay] = useState<number>(currentDate.getDate());
    const [tempYear, setTempYear] = useState<number>(currentDate.getFullYear());
    const [tempMonth, setTempMonth] = useState<number>(currentDate.getMonth());
    const [tempDay, setTempDay] = useState<number>(currentDate.getDate());
    const [isUpdateMode, setIsUpdateMode] = useState<boolean>(false);
    const [selectedEvent, setSelectedEvent] = useState<IEvent | null>(null);
    const [showAddEventModal, setShowAddEventModal] = useState<boolean>(false);

    const { eventData, categoryAndColorsData, isEventDataLoaded }: {
        eventData: IEvent[],
        categoryAndColorsData: any,
        isEventDataLoaded: boolean
    } = useSelector((state: RootState) => state.eventReducer);

    // Filter states
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [showFilters, setShowFilters] = useState<boolean>(false);

    const handleBack = () => {
        const lastPage = sessionStorage.getItem("lastPage");
        if (lastPage && lastPage.startsWith('/')) {
            navigate(lastPage);
        } else {
            navigate("/dashboard");
        }
    };
    // Filtered events based on selected categories
    const filteredEvents = useMemo(() => {
        const canShowEvent = auth ? eventData : eventData.filter((event: IEvent) => event.user_id === null);
        if (selectedCategories.length === 0) {
            return canShowEvent;
        }
        return canShowEvent.filter(event => selectedCategories.includes(event.category));
    }, [selectedCategories, eventData]);

    const openModalForDate = useCallback((date: Date, event?: IEvent): void => {
        setSelectedDate(date);
        if (event) {
            setEventsForModal([event]);
        } else {
            const filteredEventss = filteredEvents.filter((event: IEvent) => {
                const { start, end } = getEventDates(event);

                const eventStart = normalizeDate(start);
                const eventEnd = normalizeDate(end);
                eventEnd.setHours(23, 59, 59, 999);
                const selected = new Date(date);
                return selected >= eventStart && selected <= eventEnd;
            });
            setEventsForModal(filteredEventss);
        }
        setModalOpen(true);
    }, [filteredEvents, auth, getEventDates, normalizeDate]);

    const handlePrev = useCallback((): void => {
        const newDate = new Date(currentDate);
        if (view === 'day') newDate.setDate(currentDate.getDate() - 1);
        else if (view === 'week') newDate.setDate(currentDate.getDate() - 7);
        else if (view === 'month') newDate.setMonth(currentDate.getMonth() - 1);
        else if (view === 'year') newDate.setFullYear(currentDate.getFullYear() - 1);
        setCurrentDate(newDate);
        setSelectedYear(newDate.getFullYear());
        setSelectedMonth(newDate.getMonth());
        setSelectedDay(newDate.getDate());
    }, [currentDate, view]);

    const handleNext = useCallback((): void => {
        const newDate = new Date(currentDate);
        if (view === 'day') newDate.setDate(currentDate.getDate() + 1);
        else if (view === 'week') newDate.setDate(currentDate.getDate() + 7);
        else if (view === 'month') newDate.setMonth(currentDate.getMonth() + 1);
        else if (view === 'year') newDate.setFullYear(currentDate.getFullYear() + 1);
        setCurrentDate(newDate);
        setSelectedYear(newDate.getFullYear());
        setSelectedMonth(newDate.getMonth());
        setSelectedDay(newDate.getDate());
    }, [currentDate, view]);

    const handleToday = useCallback((): void => {
        const today = getSaTodayDate();
        setCurrentDate(today);
        setSelectedYear(today.getFullYear());
        setSelectedMonth(today.getMonth());
        setSelectedDay(today.getDate());
        setTempYear(today.getFullYear());
        setTempMonth(today.getMonth());
        setTempDay(today.getDate());
        setShowDatePicker(false);
    }, []);

    const handleDateDirectChange = useCallback(
        (year: number, month: number, day: number): void => {
            const newDate = new Date(year, month, day);
            setCurrentDate(newDate);

            setSelectedYear(year);
            setSelectedMonth(month);
            setSelectedDay(day);
            setShowDatePicker(false);
        },
        [selectedYear, selectedMonth, selectedDay]
    );


    const handleFetchEventsData = useCallback(async () => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            setLoading(true);
            if (auth) {
                const [eventsResponse, categoriesResponse] = await Promise.all([
                    getCalenderEvents({}, controller.signal),
                    getEventCategoryAndColors(controller.signal),
                ]);

                if (controller.signal.aborted) return;

                if (eventsResponse.status === 'ok') {
                    dispatch(fetchEventAction(eventsResponse.data ?? []));
                } else {
                    showSnackbar(eventsResponse.message, 'danger');
                }

                if (categoriesResponse.status === 'ok') {
                    dispatch(fetchEventCategoryAndColorsAction(categoriesResponse.data ?? []));
                } else {
                    showSnackbar(categoriesResponse.message, 'danger');
                }
            } else {
                const response = await getPublicCalendarJsonData(controller.signal);
                if (controller.signal.aborted) return;
                dispatch(fetchEventAction(response.events ?? []));
                dispatch(fetchEventCategoryAndColorsAction(response.categories ?? []));
            }
        } catch (error) {
            if (isAbortedError(error)) return;
            showSnackbar(getApiErrorMessage(error, "Failed to fetch events"), "danger");
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    }, [dispatch, auth, showSnackbar]);

    useEffect(() => {
        if (!isEventDataLoaded) {
            handleFetchEventsData();
        }
        return () => {
            abortRef.current?.abort();
        };
    }, [handleFetchEventsData, isEventDataLoaded]);

    const handleMonthChange = useCallback((month: number): void => {
        setTempMonth(month);
    }, []);

    const handleYearChange = useCallback((year: number): void => {
        setTempYear(year);
    }, []);

    const handleDayChange = useCallback((day: number): void => {
        setTempDay(day);
    }, []);

    const applyDateChange = useCallback((): void => {
        handleDateDirectChange(tempYear, tempMonth, tempDay);
    }, [tempYear, tempMonth, tempDay, handleDateDirectChange]);

    // Filter handlers
    const toggleCategory = useCallback((category: string): void => {
        setSelectedCategories(prev => {
            if (prev.includes(category)) {
                return prev.filter(c => c !== category);
            } else {
                return [...prev, category];
            }
        });
    }, []);

    const clearAllFilters = useCallback((): void => {
        setSelectedCategories([]);
    }, []);

    const getDaysInMonth = useCallback((year: number, month: number): number => {
        return new Date(year, month + 1, 0).getDate();
    }, []);

    const years = useMemo(() => Array.from({ length: 21 }, (_, i) => 2016 + i), []);
    const daysInMonth = useMemo(() => getDaysInMonth(tempYear, tempMonth), [tempYear, tempMonth, getDaysInMonth]);

    const openDatePicker = useCallback(() => {
        if (!showDatePicker) {
            setTempYear(selectedYear);
            setTempMonth(selectedMonth);
            setTempDay(selectedDay);
            setShowDatePicker(true);
        } else {
            setShowDatePicker(false);
        }
    }, [selectedYear, selectedMonth, selectedDay, showDatePicker]);

    const addEventOnDateClick = useCallback(
        (date: Date): void => {
            const today = getSaTodayDate();
            const selected = normalizeDate(date);
            if (selected >= today) {
                setSelectedDate(date);
                setShowAddEventModal(true);
            } else {
                showSnackbar("Cannot create events on past dates", "warning");
            }
        },
        [showSnackbar],
    );

    const renderView = useMemo(() => {
        switch (view) {
            case 'day':
                return <DayView currentDate={currentDate} events={filteredEvents} onEventClick={openModalForDate} onMoreClick={openModalForDate} />;
            case 'week':
                return <WeekView currentDate={currentDate} events={filteredEvents} onEventClick={openModalForDate} onMoreClick={openModalForDate} />;
            case 'month':
                return <MonthView currentDate={currentDate} events={filteredEvents} onDateClick={openModalForDate} onMoreClick={openModalForDate} onEventClick={openModalForDate} addEventOnDateClick={addEventOnDateClick} />;
            case 'year':
                return <YearView currentDate={currentDate} events={filteredEvents} onMonthSelect={(monthDate: Date) => { setCurrentDate(monthDate); setView('month'); }} onDateClick={openModalForDate} />;
            default:
                return null;
        }
    }, [view, currentDate, filteredEvents, openModalForDate]);

    const getTitle = useMemo(() => {
        switch (view) {
            case 'day':
                return CustomDateFormate(currentDate);
            case 'week':
                const startOfWeek = new Date(currentDate);
                startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                return `${CustomDateFormate(startOfWeek)} - ${CustomDateFormate(endOfWeek)}`;
            case 'month':
                return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
            case 'year':
                return currentDate.getFullYear().toString();
            default:
                return '';
        }
    }, [view, currentDate]);

    const getIcon = useMemo(() => {
        switch (view) {
            case 'day': return <Sun size={20} />;
            case 'week': return <CalendarDays size={20} />;
            case 'month': return <CalendarIcon size={20} />;
            case 'year': return <LayoutGrid size={20} />;
            default: return <CalendarIcon size={20} />;
        }
    }, [view]);

    const activeFiltersCount = selectedCategories.length;

    useEffect(() => {
        if (!queryDate) {
            return;
        }
        setCurrentDate(queryDate);
        setSelectedYear(queryDate.getFullYear());
        setSelectedMonth(queryDate.getMonth());
        setSelectedDay(queryDate.getDate());
    }, [queryDate]);

    useEffect(() => {
        if (!queryEventId || !isEventDataLoaded || openedFromQueryRef.current === location.search) {
            return;
        }

        openedFromQueryRef.current = location.search;
        const event = eventData.find((item) => item.event_id === queryEventId);
        if (!event) {
            return;
        }

        openModalForDate(queryDate ?? normalizeDate(event.start_date), event);
    }, [queryEventId, queryDate, isEventDataLoaded, eventData, location.search, openModalForDate]);

    useEffect(() => {
        const from = location.state?.from;
        if (!from || from.startsWith('/manage-events')) return
        sessionStorage.setItem("lastPage", from);
    }, []);

    return (
        <div className="rounded-4 shadow-sm bg-white overflow-hidden m-0 position-relative" style={{ zIndex: auth ? 0 : 100 }}>
            {loading && <SimpleLoader showbackdrop={true} text="Loading events..." />}
            <div className="d-flex align-items-start justify-content-between flex-column p-6 flex-sm-row gap-5 bg-white px-8 px-sm-6 py-10 py-sm-6 border border-bottom-1 border-gray">
                <div className="d-flex align-items-center flex-wrap gap-5 justify-content-between">
                    <h1 className="d-flex align-items-center m-0 fw-bold text-dark fs-2">
                        <CalendarIcon className="me-2 fs-2" />
                        Calendar {currentDate.toLocaleDateString('en-US', { year: 'numeric' })}
                        <span className="badge bg-light text-muted fw-semibold ms-3 fs-8 border">
                            {SA_TIMEZONE_LABEL}
                        </span>
                    </h1>

                    <div className="position-relative flex-shrink-0">
                        <button
                            className="date-picker-button text-gray border-0 fw-semibold d-flex align-items-center justify-content-center gap-2 px-4 py-3 cursor-pointer width-100 rounded-pill"
                            onClick={openDatePicker}
                        >
                            {getIcon}
                            <span className="fw-semibold text-overflow-ellipsis overflow-hidden">{getTitle}</span>
                            <ChevronDown size={20} className={`picker-icon flex-shrink-0 ${showDatePicker ? 'rotated' : ''}`} />
                        </button>

                        {showDatePicker && (
                            <div className="date-picker-dropdown position-fixed position-md-absolute bg-white shadow-sm overflow-y-auto rounded-4 max-h-75 w-75 w-sm-50">
                                <div className="p-5">
                                    <div className="row g-3 mb-5">
                                        <div className="d-flex align-items-start flex-column col-6 col-sm-4">
                                            <p className="fs-8 text-gray-700 font-normal mb-1">Month</p>
                                            <select
                                                value={tempMonth}
                                                onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                                                className="form-select-custom p-2 rounded-3 fs-7 bg-white cursor-pointer w-100"
                                            >
                                                {Array.from({ length: 12 }, (_, i) => (
                                                    <option key={i} value={i}>
                                                        {new Date(2000, i, 1).toLocaleString('default', { month: 'long' })}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="d-flex align-items-start flex-column col-6 col-sm-4">
                                            <p className="fs-8 text-gray-700 font-normal mb-1">Year</p>
                                            <select
                                                value={tempYear}
                                                onChange={(e) => handleYearChange(parseInt(e.target.value))}
                                                className="form-select-custom p-2 rounded-3 fs-7 bg-white cursor-pointer w-100"
                                            >
                                                {years.map(year => (
                                                    <option key={year} value={year}>{year}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="d-flex align-items-start flex-column col-6 col-sm-4">
                                            <p className="fs-8 text-gray-700 font-normal mb-1">Day</p>
                                            <select
                                                value={tempDay}
                                                onChange={(e) => handleDayChange(parseInt(e.target.value))}
                                                className="form-select-custom p-2 rounded-3 fs-7 bg-white cursor-pointer w-100"
                                            >
                                                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => (
                                                    <option key={day} value={day}>{day}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="mb-5">
                                        {view === 'day' && <div className='d-flex gap-2'>
                                            <button onClick={() => {
                                                const tomorrow = new Date(tempYear, tempMonth, tempDay);
                                                tomorrow.setDate(tomorrow.getDate() + 1);
                                                handleDateDirectChange(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
                                            }} className="fs-8 fw-semibold text-center cursor-pointer quick-date-btn col-6 p-2 rounded-pill">
                                                Next Day
                                            </button>
                                            <button onClick={() => {
                                                const yesterday = new Date(tempYear, tempMonth, tempDay);
                                                yesterday.setDate(yesterday.getDate() - 1);
                                                handleDateDirectChange(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
                                            }} className="fs-8 fw-semibold text-center cursor-pointer quick-date-btn col-6 p-2 rounded-pill">
                                                Previous Day
                                            </button>
                                        </div>
                                        }
                                        {view === 'week' && <div className='d-flex gap-2'>
                                            <button onClick={() => {
                                                const nextWeek = new Date(tempYear, tempMonth, tempDay);
                                                nextWeek.setDate(nextWeek.getDate() + 7);
                                                handleDateDirectChange(nextWeek.getFullYear(), nextWeek.getMonth(), nextWeek.getDate());
                                            }} className="fs-8 fw-semibold text-center cursor-pointer quick-date-btn col-6 p-2 rounded-pill">
                                                Next Week
                                            </button>
                                            <button onClick={() => {
                                                const prevWeek = new Date(tempYear, tempMonth, tempDay);
                                                prevWeek.setDate(prevWeek.getDate() - 7);
                                                handleDateDirectChange(prevWeek.getFullYear(), prevWeek.getMonth(), prevWeek.getDate());
                                            }} className="fs-8 fw-semibold text-center cursor-pointer quick-date-btn col-6 p-2 rounded-pill">
                                                Previous Week
                                            </button>
                                        </div>}
                                    </div>

                                    <div className="d-flex gap-2 pt-4 border-top">
                                        <button onClick={() => setShowDatePicker(false)} className="cancel-btn flex-grow-1 p-2 rounded-pill fs-8 fw-semibold cursor-pointer border-0 text-center">
                                            Cancel
                                        </button>
                                        <button onClick={applyDateChange} className="apply-btn flex-grow-1 p-2 rounded-pill fs-8 fw-semibold cursor-pointer border-0 text-center">
                                            Apply Date
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="d-flex align-items-center gap-3 flex-wrap justify-content-between justify-content-sm-end flex-shrink-0 ">
                    <div className="rounded-pill d-flex align-items-center gap-2 flex-shrink-0 bg-gray-200 p-1">
                        <button className="nav-btn border-0 p-2 rounded-circle cursor-pointer d-flex align-items-center justify-content-center text-gray-700" onClick={handlePrev}>
                            <ChevronLeft size={20} />
                        </button>
                        <button className="today-btn text-white px-4 py-2 rounded-pill cursor-pointer border-0 fw-semibold fs-7" onClick={handleToday}>
                            Today
                        </button>
                        <button className="nav-btn border-0 p-2 rounded-circle cursor-pointer d-flex align-items-center justify-content-center text-gray-700" onClick={handleNext}>
                            <ChevronRight size={20} />
                        </button>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        <div className="d-flex align-items-center gap-1 bg-gray-200 rounded-pill p-1">
                            {(['day', 'week', 'month', 'year'] as ViewType[]).map(v => (
                                <button
                                    key={v}
                                    className={`view-btn ${view === v ? 'active text-white' : ' text-gray-700'} fw-semibold d-flex align-items-center justify-content-center gap-2 px-sm-4 px-2 py-2 bg-opacity-0 border-0 rounded-pill cursor-pointer fs-7`}
                                    onClick={() => setView(v)}
                                >
                                    {v === 'day' && <Sun size={20} />}
                                    {v === 'week' && <CalendarDays size={20} />}
                                    {v === 'month' && <CalendarIcon size={20} />}
                                    {v === 'year' && <LayoutGrid size={20} />}
                                    <span className="d-none d-md-inline">{v.charAt(0).toUpperCase() + v.slice(1)}</span>
                                </button>
                            ))}
                        </div>

                        {/* Filter Button */}
                        <div className="position-relative">
                            <button
                                type="button"
                                className={`filter-btn d-flex align-items-center justify-content-center gap-2 px-4 py-3 rounded-pill border-0 cursor-pointer fs-7 fw-semibold position-relative ${activeFiltersCount > 0
                                    ? "active text-white bg-primary"
                                    : "text-gray-700 bg-gray-200"
                                    }`}
                                onClick={() => setShowFilters((prev) => !prev)}
                            >
                                <Filter size={20} />

                                <span className="d-none d-md-inline">Filters</span>

                                {activeFiltersCount > 0 && (
                                    <span
                                        className="filter-bedge position-absolute bg-danger text-white fw-semibold rounded-circle d-flex align-items-center justify-content-center p-2"
                                    >
                                        {activeFiltersCount}
                                    </span>
                                )}
                            </button>

                            {showFilters && (
                                <div className="filter-dropdown position-fixed position-md-absolute rounded-4 bg-white shadow-sm overflow-y-auto">
                                    <div className="filter-header position-sticky top-0 bg-white z-index-1 p-4 border-bottom d-flex justify-content-between align-items-center">
                                        <h4 className="fs-7 m-0 fw-semibold text-gray-900">Filter by Category</h4>
                                        <div>
                                            {activeFiltersCount > 0 && (
                                                <button onClick={clearAllFilters} className="clear-filters-btn bg-opacity-0 border-0 text-info fs-8 fw-semibold cursor-pointer px-2 py-1 rounded-pill">
                                                    Clear all
                                                </button>
                                            )}
                                            <Button variant='link' className='p-0' onClick={() => setShowFilters(false)}><X size={16} /></Button>
                                        </div>
                                    </div>
                                    <div className="row g-3 p-4 w-100">
                                        {categoryAndColorsData?.filter((category: CategoryColor) => category.name !== "Internal")?.map((category: CategoryColor) => (
                                            <div key={category.name} className="col-6 col-sm-4 col-md-6">
                                                <button
                                                    className={`fs-8 fw-normal cursor-pointer filter-chip w-100 d-flex align-items-center justify-content-center gap-2 rounded-pill px-3 py-2 ${selectedCategories.includes(category.name) ? '' : 'bg-white border-1'}`}
                                                    onClick={() => toggleCategory(category.name)}
                                                    style={{
                                                        backgroundColor: selectedCategories.includes(category.name)
                                                            ? `${category.color}20`
                                                            : '#f8f9fa',
                                                        borderColor: `${category.color}`,
                                                        color: selectedCategories.includes(category.name)
                                                            ? `${category.color}`
                                                            : '#6c757d'
                                                    }}
                                                >
                                                    {selectedCategories.includes(category.name) && <Check size={12} />}
                                                    <span>{category.name}</span>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="position-sticky bottom-0 bg-white z-index-1 py-3 px-4 border-top text-center fs-8 text">
                                        {activeFiltersCount === 0 ? (
                                            <span>Showing all {eventData.length} events</span>
                                        ) : (
                                            <span>Showing {filteredEvents.length} of {eventData.length} events</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    {auth && <Button className='py-3 today-btn text-white rounded-pill' onClick={() => setShowAddEventModal(true)}>
                        <Plus size={16} /> Add Event
                    </Button>}

                    <button className="btn btn-sm btn-light" onClick={handleBack}>
                        <ChevronLeft size={16} className="me-1" /> Back
                    </button>
                </div>
            </div>

            <div className="w-100 p-4 bg-white overflow-x-auto overflow-md-x-hidden" >
                {renderView}
            </div>

            <AddorUpdateModal
                show={showAddEventModal}
                onHide={() => { setShowAddEventModal(false), setIsUpdateMode(false), setSelectedEvent(null), setSelectedDate(null) }}
                isUpdateMode={isUpdateMode}
                event={selectedEvent}
                totalEvents={eventData.length}
                selectedDate={selectedDate}
            />

            <EventModal
                show={modalOpen}
                onHide={() => { setModalOpen(false) }}
                date={selectedDate}
                events={eventsForModal}
                setSelectedEvent={setSelectedEvent}
                setIsUpdateMode={setIsUpdateMode}
                setShowAddEventModal={setShowAddEventModal}
                setEventsForModal={setEventsForModal}
                selectedEvent={selectedEvent}
            />
        </div>
    );
};

export default Calendar;